import { SESSIONS_TEST_IDS } from '@/components/admin/sessions/contracts';
import {
  criterionGroupTone,
  criterionTone,
  isVerdictReading,
  lastPassage,
  stepAt,
} from '@/lib/conversation-model';
import { thresholdLabel, verdictLabel } from '@/lib/protocol-labels';
import {
  callCostLabel,
  costLabel,
  namedValueLabel,
  objectiveStatusLabel,
  objectiveTone,
} from '@/lib/session-labels';
import type { SessionTurn } from '@matter_extractor/shared';
import { objectiveProgress, objectiveState } from '@matter_extractor/shared';
import { Tooltip } from '@pms-digital/design-system';
import { useEffect, useId, useRef, useState } from 'react';
import { ObjectivePanel } from './objective-panel';
import {
  CRITERION_TONE_TEXT,
  type ConversationSources,
  GROUP_TONE_BORDER,
  STATUS_DOT,
  STATUS_TEXT,
} from './sources';

/**
 * The sixth layer of §12.3: the snapshot of §8.7, in a sticky column.
 *
 * ## A state, therefore a column
 *
 * « C'est un état et non un événement, d'où la colonne. » The five accordions
 * answer « what happened on this turn »; this answers « where the protocol
 * stood », which is a different question and one whose answer is legible only
 * beside the fil rather than inside it. It shows the LAST turn looked at and
 * stays there (issue #172), and shows the END state until somebody has looked -
 * « une colonne vide à côté d'un transcript n'apprendrait rien ».
 *
 * ## An objective opens its matter, and a réussi leads to its turn
 *
 * Its whole line opens, 300 ms after the pointer or at once under the keyboard,
 * the panel of `objective-panel.tsx`; the panel follows the next line hovered
 * without the wait, lets the pointer in, and closes 100 ms after the pointer
 * left both. A réussi line is also a way BACK: pressed, it sends the transcript
 * to the last turn at or before this one where the objective became réussi
 * (`lastPassage`), which is the turn that explains the state on screen. Under
 * 768 px nothing hovers, so a tap opens the panel and the way back is a link in
 * it.
 *
 * ## Nothing here is recomputed
 *
 * The snapshot is authoritative (§8.7): counters, statuses, matter, scores and
 * series are READ off the turn. The one thing added is the frozen protocol's own
 * numbers - the cap, the attempts allowed, the empty answers allowed - because a
 * counter without its ceiling says nothing, and the ceiling is not state.
 */

export interface StateColumnProps {
  /** The turn the column is showing - the last one looked at, or the last of all. */
  turn: SessionTurn | undefined;
  /** 1-based number of that turn. */
  number: number;
  /** True while nobody has looked at a turn: the column is showing where the protocol ENDED. */
  atEnd: boolean;
  sources: ConversationSources;
  /** A réussi line pressed: scroll to that turn, light it, and show it here. */
  onGoTo: (turn: number) => void;
}

/** The panel open, the line it stands against, and the width it was opened at. */
interface OpenPanel {
  readonly objectiveId: string;
  readonly anchor: HTMLElement;
  readonly narrow: boolean;
}

/** The hover delay of §12.3, and the grace that lets the pointer cross to the panel. */
const OPEN_DELAY_MS = 300;
const CLOSE_DELAY_MS = 100;

/** Under 768 px nothing hovers (§12.3). `matchMedia` is absent from jsdom, hence the guard. */
function isNarrow(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia('(max-width: 767px)').matches;
}

/** Disarm one of the column's two timers, if it is armed. */
function clearTimer(timer: { current: number | undefined }): void {
  if (timer.current !== undefined) window.clearTimeout(timer.current);
  timer.current = undefined;
}

/** Is `target` inside the open panel - where the pointer or the focus may go without closing it. */
function inPanel(target: EventTarget | null): boolean {
  return (
    target instanceof Element &&
    target.closest(`[data-testid="${SESSIONS_TEST_IDS.stateObjectivePanel}"]`) !== null
  );
}

export function StateColumn({ turn, number, atEnd, sources, onGoTo }: StateColumnProps) {
  const titleId = useId();

  if (turn === undefined) {
    return (
      <p data-testid={SESSIONS_TEST_IDS.state} className="text-gray-600 text-small">
        Cette session n’a joué aucun tour : il n’y a pas d’état à rejouer.
      </p>
    );
  }

  /*
    The card is a SHELL that outlives the turn shown, and the box that scrolls
    is in the shell (issue #226, H130): the `key` of the sections is what
    remounts on every turn, so a scroll box inside it would be thrown away with
    its `scrollTop` and the column would jump back to its top each time the
    pointer crossed another turn. From 768 px the card is a flex column bounded
    by the aside, and only the box under the header gives way (`min-h-0`).
  */
  return (
    <div
      data-testid={SESSIONS_TEST_IDS.state}
      data-turn={atEnd ? '' : number}
      className="overflow-hidden rounded-md border border-gray-200 bg-white md:flex md:min-h-0 md:flex-col"
    >
      <header className="flex items-baseline justify-between gap-2 border-gray-100 border-b bg-gray-50 px-3.5 py-2.5 md:shrink-0">
        <h2
          id={titleId}
          className="font-semibold text-gray-600 text-caption uppercase tracking-wide"
        >
          État du protocole
        </h2>
        <span className="font-semibold text-bleu-nuit text-caption">
          {atEnd ? 'à la fin' : `après le tour ${number}`}
        </span>
      </header>
      {/*
        Reached by Tab and scrolled by the arrows: Variables and Séries de
        garde-fous carry no control, so without a stop of its own the keyboard
        could not read the bottom of the column. Named by the card's title.
      */}
      <section
        data-testid={SESSIONS_TEST_IDS.stateScroll}
        aria-labelledby={titleId}
        // biome-ignore lint/a11y/noNoninteractiveTabindex: a scrollable region must take the focus, or the keyboard cannot scroll it (WCAG 2.1.1)
        tabIndex={0}
        // `ring-inset`: the box is flush with a card in `overflow-hidden`, which would crop an outer ring.
        className="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit focus-visible:ring-inset md:min-h-0 md:overflow-y-auto"
      >
        {/*
          Keyed on the turn shown, so an objective's panel and its timers never
          outlive the snapshot they were reading.
        */}
        <StateSections
          key={atEnd ? 'fin' : number}
          turn={turn}
          number={number}
          atEnd={atEnd}
          sources={sources}
          onGoTo={onGoTo}
        />
      </section>
    </div>
  );
}

/**
 * The sections of the card - the snapshot itself - and the state that must not
 * survive it: the open objective panel and the two timers that open and close it.
 */
function StateSections({
  turn,
  number,
  atEnd,
  sources,
  onGoTo,
}: Omit<StateColumnProps, 'turn'> & { turn: SessionTurn }) {
  const [panel, setPanel] = useState<OpenPanel | null>(null);
  const openTimer = useRef<number | undefined>(undefined);
  const closeTimer = useRef<number | undefined>(undefined);

  const show = (objectiveId: string, anchor: HTMLElement): void => {
    clearTimer(openTimer);
    clearTimer(closeTimer);
    setPanel({ objectiveId, anchor, narrow: isNarrow() });
  };

  const closeSoon = (): void => {
    clearTimer(closeTimer);
    closeTimer.current = window.setTimeout(() => setPanel(null), CLOSE_DELAY_MS);
  };

  // No timer outlives the sections: they are remounted on every turn shown.
  useEffect(
    () => () => {
      clearTimer(openTimer);
      clearTimer(closeTimer);
    },
    [],
  );

  /*
    Escape closes and gives the focus back to the line, in that order - the one
    `floating-panel.ts` holds: the focus returns BEFORE the close, and the close
    is written last so the line's own `onFocus` (which reopens) loses to it. A
    press outside both closes too, which is how a tap dismisses it on a phone.
  */
  useEffect(() => {
    if (panel === null) return;
    const { anchor } = panel;
    function onKey(event: KeyboardEvent): void {
      if (event.key !== 'Escape') return;
      anchor.focus();
      clearTimer(openTimer);
      clearTimer(closeTimer);
      setPanel(null);
    }
    function onDown(event: MouseEvent): void {
      const target = event.target;
      if (!(target instanceof Node) || anchor.contains(target) || inPanel(target)) return;
      setPanel(null);
    }
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [panel]);

  const found = stepAt(sources.detail.cascade.protocol, turn.stepId);
  const { snapshot } = turn;
  const steps = sources.detail.cascade.protocol?.content.steps ?? [];
  const held = new Set(snapshot.objectives.map((entry) => entry.objectiveId));
  /*
    The steps the SNAPSHOT has state for, plus the current one - never « the
    ones before the current rank ». A `go-to-step` and a replay both send the
    session backwards (§8.4), so the rank in the protocol says nothing about
    what has been visited: after a jump from step 4 back to step 2 the rule by
    rank hid the succeeded objectives of steps 3 and 4, which the snapshot was
    carrying all along, where §12.3 asks for « le statut et la matière de chaque
    objectif ». The current step is kept even with nothing on it yet, because a
    step just entered is exactly the one somebody is watching.

    The rank travels with the step: it is the rank in the PROTOCOL, and a filter
    would otherwise renumber the list it produced.
  */
  const walked = steps
    .map((step, rank) => ({ step, index: rank + 1 }))
    .filter(
      ({ step }) =>
        step.id === turn.stepId || step.objectives.some((objective) => held.has(objective.id)),
    );
  // The objective the open panel reads, found among the lines drawn - a line
  // that is not drawn has no panel to hold.
  const openPanel = (() => {
    if (panel === null) return null;
    for (const { step, index } of walked) {
      const objective = step.objectives.find((entry) => entry.id === panel.objectiveId);
      if (objective !== undefined) return { ...panel, objective, stepIndex: index };
    }
    return null;
  })();
  const variables = Object.entries(snapshot.variables);
  const streaks = Object.entries(snapshot.guardrailStreaks);

  return (
    <>
      <section className="flex flex-col gap-1.5 border-gray-100 border-b px-3.5 py-2.5">
        <p className="font-bold text-body text-bleu-nuit">
          {found === null ? snapshot.stepId : `Étape ${found.index} · ${found.step.name}`}
        </p>
        <dl className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-0.5 text-small">
          <Fact label="Essai">
            {snapshot.attempt}
            {found === null ? '' : ` sur ${found.step.rules.attempts}`}
          </Fact>
          <Fact label="Tours dans l’étape">
            {snapshot.turnInStep}
            {found?.step.rules.maxTurns == null ? '' : ` sur ${found.step.rules.maxTurns}`}
          </Fact>
          <Fact label="Réponses vides d’affilée" warn={snapshot.emptyAnswerStreak > 0}>
            {snapshot.emptyAnswerStreak}
            {found === null ? '' : ` sur ${found.step.rules.emptyAnswersAllowed}`}
          </Fact>
          <Fact label="Coût de l’étape">
            {callCostLabel(snapshot.stepCostEur)}
            {sources.budgetPerStepEur === null ? '' : ` sur ${costLabel(sources.budgetPerStepEur)}`}
          </Fact>
        </dl>
      </section>

      <section className="flex flex-col gap-1.5 border-gray-100 border-b px-3.5 py-2.5">
        <h3 className="font-semibold text-gray-600 text-caption uppercase tracking-wide">
          Objectifs et matière
        </h3>
        {steps.length === 0 ? (
          <p className="text-gray-500 text-caption">
            Le protocole joué n’est plus lisible : ses objectifs ne peuvent pas être nommés.
          </p>
        ) : walked.length === 0 ? (
          <p className="text-gray-500 text-caption">
            Aucun objectif du protocole joué ne porte d’état sur cet instantané.
          </p>
        ) : (
          walked.map(({ step, index }) => (
            <div key={step.id} className="flex flex-col gap-1">
              <p className="mt-1 text-gray-500 text-caption uppercase tracking-wide first:mt-0">
                Étape {index} · {step.name}
              </p>
              {step.objectives.map((objective) => {
                const state = objectiveState(snapshot, objective.id);
                const progress = objectiveProgress(objective, state);
                const live = state.extracts.filter((extract) => !extract.invalidated).length;
                // The engine's status stays on `data-status`; the INK is the
                // tone, a réussi validated at the passable bar in yellow (#112).
                const tone = objectiveTone(state);
                const target = lastPassage(sources.detail.turns, objective.id, number);
                const open = panel?.objectiveId === objective.id;
                return (
                  /*
                    A BUTTON, every line and not only the réussi ones: each opens
                    its panel under the keyboard and under a finger, and a réussi
                    one also leads to its turn. The label is the mockup's - name,
                    status, where it leads - the panel carrying the rest.
                  */
                  <button
                    type="button"
                    key={objective.id}
                    data-testid={SESSIONS_TEST_IDS.stateObjective}
                    data-objective-id={objective.id}
                    data-status={state.status}
                    data-target={target ?? ''}
                    aria-haspopup="dialog"
                    aria-expanded={open}
                    aria-label={`${objective.name}, ${objectiveStatusLabel(tone)}${
                      target === null ? '' : `, aller au tour ${target}`
                    }`}
                    onMouseEnter={(event) => {
                      const anchor = event.currentTarget;
                      clearTimer(closeTimer);
                      if (panel !== null) {
                        // An open panel follows the next line at once.
                        if (!open) show(objective.id, anchor);
                        return;
                      }
                      clearTimer(openTimer);
                      openTimer.current = window.setTimeout(
                        () => show(objective.id, anchor),
                        OPEN_DELAY_MS,
                      );
                    }}
                    onMouseLeave={(event) => {
                      clearTimer(openTimer);
                      if (panel !== null && !inPanel(event.relatedTarget)) closeSoon();
                    }}
                    onFocus={(event) => show(objective.id, event.currentTarget)}
                    onBlur={(event) => {
                      if (!inPanel(event.relatedTarget)) closeSoon();
                    }}
                    onClick={(event) => {
                      if (isNarrow()) {
                        // A tap opens the panel, and the way back is its link.
                        if (open) setPanel(null);
                        else show(objective.id, event.currentTarget);
                        return;
                      }
                      if (target === null) return;
                      setPanel(null);
                      onGoTo(target);
                    }}
                    className={`-mx-1.5 grid grid-cols-[0.75rem_minmax(0,1fr)_auto] items-baseline gap-x-2 gap-y-0.5 rounded px-1.5 py-0.5 text-left text-small focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit ${
                      target === null
                        ? `cursor-default ${open ? 'bg-gray-50' : ''}`
                        : `cursor-pointer hover:bg-bleu-nuit-tint focus-visible:bg-bleu-nuit-tint ${
                            open ? 'bg-bleu-nuit-tint' : ''
                          }`
                    }`}
                  >
                    <span
                      aria-hidden="true"
                      className={`size-2.5 shrink-0 rounded-full ${STATUS_DOT[tone]}`}
                    />
                    <span className="min-w-0 font-semibold text-gray-900">
                      {objective.name}{' '}
                      <span className="font-normal text-gray-500 text-caption">
                        {objective.mandatory ? 'obligatoire' : 'optionnel'}
                      </span>
                    </span>
                    {/* The status word is in the panel now, which replaced this count's bubble. */}
                    <span className={`text-caption tabular-nums ${STATUS_TEXT[tone]}`}>
                      {live} extrait{live > 1 ? 's' : ''}
                    </span>
                    <span className="col-start-2 col-end-4 flex flex-wrap gap-x-3 gap-y-0.5 text-gray-600 text-caption tabular-nums">
                      {progress.groups.map((group) => {
                        const cells = group.criteria.map((criterion) => {
                          // The two forms of §8.2, told apart by the same two
                          // readers the Objectifs layer uses. The COLUMN is
                          // narrow, so a score shows its number alone: its band
                          // is in that layer, where there is room for the
                          // sentence that names it. The INK carries the tone
                          // (#110) - passable in yellow - and the bound that
                          // makes it passable is named in that layer too.
                          const bar = thresholdLabel(criterion.threshold, criterion.plancher);
                          return (
                            <span key={criterion.criterionId}>
                              {sources.criteria.get(criterion.criterionId)?.content.name ??
                                criterion.criterionId}{' '}
                              <b className={CRITERION_TONE_TEXT[criterionTone(criterion)]}>
                                {isVerdictReading(criterion)
                                  ? verdictLabel(criterion.verdict)
                                  : (criterion.score ?? '-')}
                              </b>
                              {bar === null ? null : <span> {bar}</span>}
                            </span>
                          );
                        });
                        // A criterion that stands alone reads as it always did; an
                        // « l'un de » group (#181) is framed, its members joined by
                        // « ou », so a short criterion beside a filled one is not
                        // mistaken for what keeps the objective empty.
                        if (group.orGroup === null || group.criteria.length < 2) return cells;
                        const tone = criterionGroupTone(group.criteria.map(criterionTone));
                        return (
                          <span
                            key={group.criteria.map((criterion) => criterion.criterionId).join('+')}
                            data-testid={SESSIONS_TEST_IDS.criterionGroup}
                            data-or-group={group.orGroup}
                            data-tone={tone}
                            className={`inline-flex flex-wrap items-baseline gap-x-2 border-l-2 pl-2 ${GROUP_TONE_BORDER[tone]}`}
                          >
                            <span className="font-bold text-gray-500 uppercase tracking-wide">
                              l’un de
                            </span>
                            {cells.flatMap((cell, rank) =>
                              rank === 0
                                ? [cell]
                                : [
                                    <span
                                      // biome-ignore lint/suspicious/noArrayIndexKey: positional separators, one between two neighbouring criteria
                                      key={`or-${rank}`}
                                      className="font-bold text-gray-500 uppercase tracking-wide"
                                    >
                                      ou
                                    </span>,
                                    cell,
                                  ],
                            )}
                          </span>
                        );
                      })}
                    </span>
                  </button>
                );
              })}
            </div>
          ))
        )}
        {openPanel === null ? null : (
          <ObjectivePanel
            anchor={openPanel.anchor}
            objective={openPanel.objective}
            stepIndex={openPanel.stepIndex}
            turns={sources.detail.turns}
            number={number}
            atEnd={atEnd}
            target={lastPassage(sources.detail.turns, openPanel.objective.id, number)}
            narrow={openPanel.narrow}
            onGoTo={(to) => {
              setPanel(null);
              onGoTo(to);
            }}
            onPointerIn={() => clearTimer(closeTimer)}
            // Back onto its line, the line's own `onMouseEnter` cancels this.
            onPointerOut={closeSoon}
            onFocusOut={(event) => {
              const to = event.relatedTarget;
              if (inPanel(to) || (to instanceof Node && openPanel.anchor.contains(to))) return;
              closeSoon();
            }}
          />
        )}
      </section>

      <section className="flex flex-col gap-1.5 border-gray-100 border-b px-3.5 py-2.5">
        <h3 className="font-semibold text-gray-600 text-caption uppercase tracking-wide">
          Variables
        </h3>
        {variables.length === 0 ? (
          <p className="text-gray-500 text-caption">
            La session n’avait encore écrit aucune valeur.
          </p>
        ) : (
          <dl className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-0.5 text-small">
            {variables.map(([blockId, variable]) => (
              <Fact key={blockId} label={variable.name}>
                {/*
                  « Une case cochée se lit "coché" et jamais `true` » (§12.2):
                  the value is stored as a boolean because that is what it is,
                  and the rendering is the screen's business. The wording comes
                  from `session-labels.ts`, where the parcours takes it too - a
                  second spelling of one rule is how « décoché » came to stand
                  beside « non coché » for the same boolean.
                */}
                <Tooltip content={String(variable.value)}>
                  <span className="block max-w-42.5 truncate">
                    {namedValueLabel(variable.value)}
                  </span>
                </Tooltip>
              </Fact>
            ))}
          </dl>
        )}
      </section>

      <section className="flex flex-col gap-1.5 px-3.5 py-2.5">
        <h3 className="font-semibold text-gray-600 text-caption uppercase tracking-wide">
          Séries de garde-fous
        </h3>
        {streaks.length === 0 ? (
          <p className="text-gray-500 text-caption">Aucune série en cours.</p>
        ) : (
          <dl className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-0.5 text-small">
            {streaks.map(([guardrailId, streak]) => {
              const known = sources.guardrails.find((entry) => entry.id === guardrailId);
              return (
                <Fact key={guardrailId} label={known?.detection ?? guardrailId} warn={streak > 0}>
                  {streak}
                  {known === undefined ? '' : ` / ${known.consecutiveTurns}`}
                </Fact>
              );
            })}
          </dl>
        )}
      </section>
    </>
  );
}

/** One `libellé / valeur` pair of the column, right-aligned on tabular figures. */
function Fact({
  label,
  warn,
  children,
}: {
  label: string;
  warn?: boolean;
  children: React.ReactNode;
}) {
  return (
    <>
      <dt className="min-w-0 break-words text-gray-600">{label}</dt>
      <dd
        className={`m-0 text-right tabular-nums ${
          warn === true ? 'font-semibold text-jaune-deep' : 'text-gray-900'
        }`}
      >
        {children}
      </dd>
    </>
  );
}
